import ErrorIcon from '@mui/icons-material/Error';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import type { SupportDoc } from '../../components/TenderDetails/types';
import { useTenderDetails } from '../../contexts/TenderDetailsContext';
import SharedDropzone from '../common/SharedDropzone';
import { type Column, StandardTable } from '../common/UI/StandardTable';
import { getLoadingKey } from './utils';
import WorkflowStep from './WorkflowStep';

const UploadPartnerDocumentsStep: React.FC = () => {
  const {
    isPartnerView,
    openStep,
    setOpenStep,
    user_docs,
    account,
    tender,
    handleFileUpload,
    loadingStates,
    processingFiles,
    handleDownload,
    handleDeleteSupportDoc,
  } = useTenderDetails();

  if (!isPartnerView) {
    return null;
  }

  const processingColumns: Column<string>[] = [
    {
      id: 'fileName',
      label: '',
      render: (fileName) => fileName,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: () => <CircularProgress size={16} />,
    },
  ];

  const uploadedDocs = user_docs.filter(
    (d: SupportDoc) => d.doc_type === 'partner_doc' && d.uploaderId === account?.uid
  );

  const userDocsColumns: Column<SupportDoc>[] = [
    {
      id: 'fileName',
      label: 'File Name',
      render: (doc) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {doc.originalFilename || doc.name}
          </Typography>
          {(doc.conversionStatus === 'failed' || doc.embeddingStatus === 'failed') && (
            <Tooltip title={doc.conversionFailureReason || 'Document processing failed'}>
              <ErrorIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (doc) => (
        <>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              handleDownload(
                doc.downloadURL ||
                  (doc.storageUri?.startsWith('gs://') || doc.gcsPath?.startsWith('gs://')
                    ? doc.storageUri || doc.gcsPath || ''
                    : `gs://${account?.actualDefaultBucketName}/${doc.storageUri || doc.gcsPath || ''}`),
                'support',
                doc.originalFilename || doc.name || 'document'
              )
            }
          >
            Download
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            sx={{ ml: 1 }}
            disabled={!!loadingStates[`delete_${doc.originalFilename || doc.name}`]}
            onClick={() => handleDeleteSupportDoc(doc)}
          >
            {loadingStates[`delete_${doc.originalFilename || doc.name}`] ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Delete'
            )}
          </Button>
        </>
      ),
    },
  ];

  return (
    <WorkflowStep
      stepNumber={3}
      expanded={openStep === 3}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 3 : null)}
      title="Upload Partner Documents"
      description="Upload CVs, capability statements, and other documents requested by the tender owner."
      isComplete={user_docs.some(
        (d: SupportDoc) => d.doc_type === 'partner_doc' && d.uploaderId === account?.uid
      )}
    >
      <Box sx={{ mt: 2 }}>
        <SharedDropzone
          onFilesSelected={(files: File[]) => {
            for (const file of files) {
              handleFileUpload(file, 'partner_doc', {});
            }
          }}
          type="tender_docs"
          dropzoneText="Drop partner documents here, or click to select"
          isLoading={!!loadingStates[getLoadingKey(tender.id, 'upload', 'partner_doc')]}
        />

        {/* Processing Files List for Partner Docs */}
        {processingFiles?.partner_doc?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              Processing Uploads:
            </Typography>
            <StandardTable
              columns={processingColumns}
              data={processingFiles.partner_doc}
              keyExtractor={(fileName) => fileName}
              stickyHeader={false}
            />
          </Box>
        )}

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Your Uploaded Documents
          </Typography>
          <Box sx={{ mt: 1 }}>
            <StandardTable
              columns={userDocsColumns}
              data={uploadedDocs}
              keyExtractor={(doc) => doc.id || doc.name || ''}
            />
          </Box>
        </Box>
      </Box>
    </WorkflowStep>
  );
};

export default UploadPartnerDocumentsStep;
